import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Snackbar, Typography } from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { useWorkspace } from '../context/WorkspaceContext';
import UpgradePremiumCta from '../components/billing/UpgradePremiumCta';
import RouteSuspenseFallback from '../components/RouteSuspenseFallback';
import { DashboardModuleRoot } from '../components/layout/DashboardLayout';
import ReportQuerySidebar from '../components/reports/ReportQuerySidebar';
import ReportVisualizationCanvas from '../components/reports/ReportVisualizationCanvas';
import FrictionInsightsPanel from '../components/reports/FrictionInsightsPanel';
import {
  buildCallablePayload,
  createDefaultQueryConfig,
  downloadReportCsv,
} from '../lib/reports/reportQueryConfig';

const generateReportData = httpsCallable(functions, 'generateReportData');

/**
 * Analytics Studio — Metabase-style parameter sidebar + visualization canvas.
 * @param {{ userData?: { companyId?: string }, onGoBilling?: () => void }} props
 */
export default function ReportsHub({ onGoBilling }) {
  const { workspace, loading: workspaceLoading } = useWorkspace();

  const [queryConfig, setQueryConfig] = useState(() => createDefaultQueryConfig('tickets'));
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [viewMode, setViewMode] = useState('chart');
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [error, setError] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);

  const billingActive =
    workspace?.billing &&
    typeof workspace.billing === 'object' &&
    workspace.billing.status === 'active';

  const runQuery = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = buildCallablePayload(queryConfig);
      const result = await generateReportData(payload);
      const data = result?.data || {};
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setMeta(data.meta || null);
      setHasRun(true);
    } catch (e) {
      console.error('ReportsHub generateReportData:', e);
      const message =
        e?.details ||
        (typeof e?.message === 'string' && e.message !== 'INTERNAL' ? e.message : '') ||
        'Could not run report. Ensure you are signed in as a company admin or manager.';
      setError(String(message));
      setToastOpen(true);
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [queryConfig]);

  const handleDownloadCSV = useCallback(() => {
    if (!rows?.length) return;
    downloadReportCsv(rows, queryConfig.collectionName);
  }, [rows, queryConfig.collectionName]);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => !prev);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setIsCanvasFullscreen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isCanvasFullscreen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsCanvasFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCanvasFullscreen]);

  if (workspaceLoading) {
    return <RouteSuspenseFallback />;
  }

  if (!billingActive) {
    return (
      <DashboardModuleRoot>
        <Box sx={{ p: 3 }}>
          <UpgradePremiumCta featureLabel="Reports & Analytics Studio" onGoBilling={onGoBilling} />
        </Box>
      </DashboardModuleRoot>
    );
  }

  return (
    <DashboardModuleRoot sx={{ flexDirection: 'row', height: '100%', overflow: 'hidden' }}>
      {!isCanvasFullscreen && (
        <ReportQuerySidebar
          queryConfig={queryConfig}
          onChange={setQueryConfig}
          onRunQuery={runQuery}
          loading={loading}
          collapsed={isSidebarCollapsed}
        />
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          transition: 'flex 0.25s ease',
        }}
      >
        {!isCanvasFullscreen && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <AssessmentOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={700}>
              Analytics Studio
            </Typography>
          </Box>
        )}

        {!isCanvasFullscreen && <FrictionInsightsPanel />}

        {error && !isCanvasFullscreen && (
          <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <ReportVisualizationCanvas
          rows={rows}
          meta={meta}
          queryConfig={queryConfig}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          hasRun={hasRun}
          loading={loading}
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={handleToggleSidebar}
          isCanvasFullscreen={isCanvasFullscreen}
          onToggleFullscreen={handleToggleFullscreen}
          onExportCsv={handleDownloadCSV}
          exportDisabled={!rows?.length}
        />
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setToastOpen(false)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </DashboardModuleRoot>
  );
}
